import { CommonModule, isPlatformBrowser } from '@angular/common';
import { AuthService, writeWebsiteBuilderCookie } from '../../services/auth.service';
import { HttpService } from '../../services/http.service';
import { ToastService } from '../../services/toast.service';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, PLATFORM_ID, ViewChild } from '@angular/core';
import { environment } from '../../../environments/environment';
import { getDeviceHash } from '../../services/account-security.service';
import { LoginTwoFactorStepComponent } from '../../shared/login-two-factor-step/login-two-factor-step.component';
import { UtmService } from '../../services/utm.service';

@Component({
  selector: 'app-sellar-login',
  imports: [CommonModule, ReactiveFormsModule, RouterModule, LoginTwoFactorStepComponent],
  templateUrl: './sellar-login.component.html',
  styleUrl: './sellar-login.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // SSR HTML stays for first paint; skip hydrating this form so Continue/input
  // bind on the client. Event-replay hydration was leaving dead jsaction clicks.
  host: { ngSkipHydration: 'true' },
})
export class SellarLoginComponent {
  @ViewChild(LoginTwoFactorStepComponent) twoFactorStep?: LoginTwoFactorStepComponent;

  loginForm: FormGroup;
  loading = false;
  /** Step 2: small popup for password after Continue with email */
  showPasswordModal = false;
  show2FA = false;
  requiresAuthenticator2FA = false; // Track if authenticator 2FA is required
  sellerId: string | null = null;
  sellerEmail: string = '';
  errorMessage = '';
  /** Customer storefront origin — Next.js (:3000 local, merchantsbazaar.com prod). */
  readonly storefrontUrl = String(
    (environment as { storefrontUrl?: string }).storefrontUrl ||
      (environment as { frontendUrl?: string }).frontendUrl ||
      'https://merchantsbazaar.com',
  ).replace(/\/$/, '');
  readonly storefrontHomeUrl = this.storefrontUrl + '/';
  readonly storefrontLoginUrl = this.storefrontUrl + '/login';

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private httpService: HttpService,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private utmService: UtmService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
    });
    this.utmService.captureUtmParameters();
  }

  /** Enter on email → Continue; Enter on password in modal → login */
  onLoginFormSubmit(): void {
    if (this.show2FA) return;
    if (!this.showPasswordModal) {
      this.onContinueWithEmail();
      return;
    }
    this.onSubmit();
  }

  onContinueWithEmail(): void {
    const emailCtrl = this.loginForm.get('email');
    if (isPlatformBrowser(this.platformId)) {
      const typed = (
        document.getElementById('seller-login-email') as HTMLInputElement | null
      )?.value?.trim();
      if (typed && typed !== emailCtrl?.value) {
        emailCtrl?.setValue(typed);
      }
    }
    emailCtrl?.markAsTouched();
    emailCtrl?.updateValueAndValidity({ emitEvent: false });
    if (!emailCtrl?.valid) {
      this.errorMessage = 'Enter a valid email address to continue.';
      this.cdr.detectChanges();
      return;
    }
    this.errorMessage = '';
    this.loginForm.get('password')?.setValue('');
    this.showPasswordModal = true;
    this.cdr.detectChanges();
  }

  closePasswordModal(): void {
    this.showPasswordModal = false;
    this.loginForm.patchValue({ password: '' });
    this.cdr.markForCheck();
  }

  onSubmit() {
    if (this.loginForm.invalid) return;

    this.loading = true;
    this.errorMessage = '';
    this.cdr.markForCheck();
    // Use longer timeout for seller login (120 seconds) since it may involve 2FA email sending
    const payload = {
      ...this.loginForm.value,
      deviceHash: getDeviceHash(),
      attribution: this.utmService.getUtmAttribution() || undefined,
    };
    this.httpService.post('seller/login', payload, 120000).subscribe({
      next: (response: any) => {
        this.loading = false;

        // Check if 2FA is required
        if (response.requires2FA) {
          this.showPasswordModal = false;
          this.show2FA = true;
          this.sellerId = response.sellerId;
          this.sellerEmail = response.email;
          this.requiresAuthenticator2FA = response.requiresAuthenticator2FA || false;
          this.cdr.markForCheck();
          return;
        }

        // Normal login (2FA not enabled)
        if (response?.token && isPlatformBrowser(this.platformId)) {
          localStorage.setItem('token', response.token);
          writeWebsiteBuilderCookie(response.token);
        }

        this.authService.setUser(response);
        this.navigateAfterLogin();
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.loading = false;
        const isTimeout = err.status === 0 ||
                         err.name === 'TimeoutError' ||
                         (err.error && err.error.message && err.error.message.includes('timeout'));

        if (isTimeout) {
          this.errorMessage = 'Request took too long. If you have 2FA enabled, please check your email for the verification code. Otherwise, try logging in again.';
          this.toastService.warning('Request timeout. If 2FA is enabled, check your email for verification code.');
        } else {
          this.errorMessage = err.error?.message || 'Login failed. Please try again.';
          this.toastService.error(this.errorMessage);
        }
        if (!environment.production) {
          console.error('Login error:', err);
        }
        this.cdr.markForCheck();
      },
    });
  }

  onVerify2FA(code: string) {
    if (!this.sellerId || !code || code.length !== 6) return;

    this.loading = true;
    this.errorMessage = '';
    this.cdr.markForCheck();

    this.httpService.post('seller/verify-2fa-login', {
      sellerId: this.sellerId,
      otp: code,
      deviceHash: getDeviceHash(),
      attribution: this.utmService.getUtmAttribution() || undefined,
    }).subscribe({
      next: (response: any) => {
        this.loading = false;

        if (response?.token && isPlatformBrowser(this.platformId)) {
          localStorage.setItem('token', response.token);
          writeWebsiteBuilderCookie(response.token);
        }

        this.authService.setUser(response);
        this.navigateAfterLogin();
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.loading = false;
        this.errorMessage = err.error?.message || 'Invalid verification code. Please try again.';
        this.twoFactorStep?.resetDigits();
        this.cdr.markForCheck();
      },
    });
  }

  resend2FAOTP() {
    // Don't allow resend for authenticator 2FA
    if (this.requiresAuthenticator2FA) {
      this.errorMessage = 'Authenticator codes cannot be resent. Please use the code from your authenticator app.';
      this.cdr.markForCheck();
      return;
    }

    if (!this.sellerId) return;

    this.loading = true;
    this.errorMessage = '';
    this.cdr.markForCheck();

    this.httpService.post('seller/resend-2fa-otp', {
      sellerId: this.sellerId
    }).subscribe({
      next: (response: any) => {
        this.loading = false;
        this.toastService.success(response.message || 'New verification code has been sent to your email');
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.loading = false;
        const errorMsg = err.error?.message || 'Failed to resend code. Please try again.';
        if (err.error?.error === 'EMAIL_SEND_FAILED') {
          this.toastService.error('OTP was generated but email sending failed. Please contact support or try again.');
        } else {
          this.toastService.error(errorMsg);
        }
        this.errorMessage = errorMsg;
        this.cdr.markForCheck();
      },
    });
  }

  backToLogin() {
    this.show2FA = false;
    this.showPasswordModal = false;
    this.requiresAuthenticator2FA = false;
    this.sellerId = null;
    this.sellerEmail = '';
    this.errorMessage = '';
    this.cdr.markForCheck();
  }

  getMaskedEmail(): string {
    if (!this.sellerEmail) return '';
    const [local, domain] = this.sellerEmail.split('@');
    return `${local.substring(0, 2)}***@${domain}`;
  }

  private navigateAfterLogin(): void {
    const raw = (this.route.snapshot.queryParamMap.get('redirect') || '').trim();
    if (
      raw.startsWith('/seller-dashboard') &&
      !raw.startsWith('//') &&
      !raw.includes('://')
    ) {
      this.router.navigateByUrl(raw);
      return;
    }
    this.router.navigate(['/seller-dashboard/']);
  }
}
